import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import { fmtDateTime } from '../../../utils/ruDates';

// Раньше раздел показывал только счётчики за сегодня: видно, что дизлайков N,
// но не видно, на что их поставили. Оценки писались всегда — просто наружу не
// отдавались. Теперь это журнал: вопрос, ответ, вердикт, разбор и выгрузка.

const VOTE_LABEL = { like: '👍 лайк', dislike: '👎 дизлайк' };
const REVIEW_LABEL = {
  new: 'не разобрано',
  reviewed: 'разобрано',
  kb_created: 'внесено в базу знаний',
};
const REVIEW_COLOR = { new: '#f39c12', reviewed: '#27ae60', kb_created: '#2980b9' };

const Card = ({ children, style }) => (
  <div style={{
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(26,31,54,0.08)',
    padding: 18,
    ...style,
  }}>{children}</div>
);

const Tile = ({ label, value, color }) => (
  <Card style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1a1f36' }}>{value}</div>
  </Card>
);

const Field = ({ label, children }) => (
  <label style={{ display: 'grid', gap: 4 }}>
    <span style={{ fontSize: 12, color: '#666' }}>{label}</span>
    {children}
  </label>
);

const inputStyle = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 };

function AdminAiSatisfaction() {
  const { request, authFetch, API_BASE_URL, showStatus } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    vote: '',
    review_status: '',
    search: '',
  });

  const buildQuery = useCallback(() => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    return qs;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await request(`/api/v1/admin/ai-chat/feedback?${buildQuery()}`));
    } catch (error) {
      logger.error('Не удалось загрузить оценки чата', error);
      showStatus('error', 'Ошибка загрузки оценок');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setReview = async (messageId, reviewStatus) => {
    try {
      await request(`/api/v1/admin/ai-chat/feedback/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify({ review_status: reviewStatus }),
      });
      load();
    } catch (e) {
      showStatus('error', 'Не удалось изменить статус разбора');
    }
  };

  const setNote = async (messageId, current) => {
    const note = window.prompt('Заметка админа:', current || '');
    if (note === null) return;
    try {
      await request(`/api/v1/admin/ai-chat/feedback/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify({ admin_note: note }),
      });
      load();
    } catch (e) {
      showStatus('error', 'Не удалось сохранить заметку');
    }
  };

  const removeFeedback = async (messageId) => {
    if (!window.confirm('Снять оценку с этого ответа? Сама переписка останется.')) return;
    try {
      await request(`/api/v1/admin/ai-chat/feedback/${messageId}`, { method: 'DELETE' });
      showStatus('success', 'Оценка снята');
      load();
    } catch (e) {
      showStatus('error', 'Не удалось снять оценку');
    }
  };

  const purge = async () => {
    const scope = [
      data?.date_from && data?.date_to ? `за ${data.date_from} — ${data.date_to}` : null,
      filters.vote ? `только ${VOTE_LABEL[filters.vote]}` : null,
      filters.review_status ? `только «${REVIEW_LABEL[filters.review_status]}»` : null,
    ].filter(Boolean).join(', ');
    if (!window.confirm(
      `Снять оценки ${scope || 'за весь показанный период'}?\n\n`
      + 'Сообщения не удаляются, но история оценок будет затёрта безвозвратно.'
    )) return;
    try {
      const result = await request('/api/v1/admin/ai-chat/feedback/purge', {
        method: 'POST',
        body: JSON.stringify({
          confirm: true,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          vote: filters.vote || undefined,
          review_status: filters.review_status || undefined,
        }),
      });
      showStatus('success', `Снято оценок: ${result?.affected ?? 0}`);
      load();
    } catch (e) {
      showStatus('error', 'Не удалось снять оценки');
    }
  };

  const exportCsv = async () => {
    try {
      // authFetch, а не fetch: он сам подставит токен и обновит истёкший.
      const response = await authFetch(
        `${API_BASE_URL}/api/v1/admin/ai-chat/feedback/export?${buildQuery()}`,
      );
      if (!response.ok) throw new Error('export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-chat-feedback_${data?.date_from || ''}_${data?.date_to || ''}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      logger.error('Не удалось выгрузить оценки', e);
      showStatus('error', 'Ошибка выгрузки');
    }
  };

  const s = data?.summary;

  return (
    <div className="admin-page">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Удовлетворённость чатом</h1>
        <p style={{ color: '#697386', margin: '4px 0 0', fontSize: 14 }}>
          Что спросили, что ответил агент и как это оценили
          {data?.date_from ? ` · ${data.date_from} — ${data.date_to}` : ''}
        </p>
      </div>

      {s && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
          marginBottom: 22,
        }}>
          <Tile label="Оценок всего" value={s.rated} />
          <Tile label="👍 Лайков" value={s.likes} color="#27ae60" />
          <Tile label="👎 Дизлайков" value={s.dislikes} color="#e74c3c" />
          <Tile
            label="Довольных"
            value={s.satisfaction_pct === null ? '—' : `${s.satisfaction_pct}%`}
            color={s.satisfaction_pct === null ? '#8a94a6' : (s.satisfaction_pct >= 70 ? '#27ae60' : '#e74c3c')}
          />
          <Tile
            label="Не разобрано"
            value={s.unreviewed}
            color={s.unreviewed > 0 ? '#f39c12' : '#8a94a6'}
          />
        </div>
      )}

      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="С даты">
            <input type="date" style={inputStyle} value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          </Field>
          <Field label="По дату">
            <input type="date" style={inputStyle} value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          </Field>
          <Field label="Оценка">
            <select style={inputStyle} value={filters.vote}
              onChange={e => setFilters(f => ({ ...f, vote: e.target.value }))}>
              <option value="">любая</option>
              <option value="dislike">только дизлайки</option>
              <option value="like">только лайки</option>
            </select>
          </Field>
          <Field label="Разбор">
            <select style={inputStyle} value={filters.review_status}
              onChange={e => setFilters(f => ({ ...f, review_status: e.target.value }))}>
              <option value="">любой</option>
              <option value="new">не разобрано</option>
              <option value="reviewed">разобрано</option>
              <option value="kb_created">внесено в базу знаний</option>
            </select>
          </Field>
          <Field label="Поиск">
            <input type="text" placeholder="текст, комментарий, e-mail" style={{ ...inputStyle, minWidth: 240 }}
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </Field>
          <button type="button" onClick={load} disabled={loading}
            style={{ padding: '9px 18px', border: 'none', borderRadius: 6, background: '#2c3e50', color: 'white', cursor: 'pointer' }}>
            {loading ? '...' : 'Показать'}
          </button>
          <button type="button" onClick={exportCsv}
            style={{ padding: '9px 18px', border: '1px solid #2980b9', borderRadius: 6, background: 'white', color: '#2980b9', cursor: 'pointer' }}>
            Выгрузить CSV
          </button>
          <button type="button" onClick={purge}
            style={{ padding: '9px 18px', border: '1px solid #e74c3c', borderRadius: 6, background: 'white', color: '#e74c3c', cursor: 'pointer', marginLeft: 'auto' }}>
            Снять оценки по фильтру
          </button>
        </div>
      </Card>

      {!loading && data?.items?.length === 0 && (
        <Card style={{ color: '#8a94a6' }}>За период нет оценённых ответов.</Card>
      )}

      {data?.has_more && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', background: '#fff8e1',
          borderRadius: 6, fontSize: 13, color: '#8a6d3b',
        }}>
          Показана только первая страница — итоги выше посчитаны по всему периоду.
          Сузь период или фильтр, чтобы увидеть остальное.
        </div>
      )}

      <div style={{ display: 'grid', gap: 14 }}>
        {data?.items?.map(item => (
          <Card key={item.message_id}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
              <strong style={{ color: item.vote === 'like' ? '#27ae60' : '#e74c3c' }}>
                {VOTE_LABEL[item.vote] || item.vote}
              </strong>
              <span style={{
                padding: '2px 9px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: `${REVIEW_COLOR[item.review_status] || '#7f8c8d'}20`,
                color: REVIEW_COLOR[item.review_status] || '#7f8c8d',
              }}>{REVIEW_LABEL[item.review_status] || item.review_status}</span>
              <span style={{ color: '#697386', fontSize: 13 }}>{item.user_email || '—'}</span>
              {item.agent && <span style={{ color: '#a3acb9', fontSize: 13 }}>агент: {item.agent}</span>}
              <span style={{ marginLeft: 'auto', color: '#a3acb9', fontSize: 13 }}>
                {fmtDateTime(item.voted_at)}
              </span>
            </div>

            {item.comment && (
              <div style={{
                padding: '8px 12px', background: '#fdf6e3', borderRadius: 8,
                fontSize: 13, marginBottom: 10,
              }}>
                <strong>Комментарий пользователя:</strong> {item.comment}
              </div>
            )}

            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <div style={{ color: '#8a94a6', marginBottom: 3 }}>Вопрос</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{item.question || '—'}</div>
            </div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              <div style={{ color: '#8a94a6', marginBottom: 3 }}>Ответ</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{item.answer}</div>
            </div>

            {item.admin_note && (
              <div style={{ fontSize: 13, color: '#2980b9', marginBottom: 10 }}>
                Заметка: {item.admin_note}
              </div>
            )}

            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {item.review_status !== 'reviewed' && (
                <button type="button" onClick={() => setReview(item.message_id, 'reviewed')}
                  style={{ background: 'none', border: 'none', color: '#27ae60', cursor: 'pointer', padding: 0 }}>
                  Разобрано
                </button>
              )}
              {item.review_status !== 'kb_created' && (
                <button type="button" onClick={() => setReview(item.message_id, 'kb_created')}
                  style={{ background: 'none', border: 'none', color: '#2980b9', cursor: 'pointer', padding: 0 }}>
                  Внесено в базу знаний
                </button>
              )}
              {item.review_status !== 'new' && (
                <button type="button" onClick={() => setReview(item.message_id, 'new')}
                  style={{ background: 'none', border: 'none', color: '#f39c12', cursor: 'pointer', padding: 0 }}>
                  Вернуть в неразобранные
                </button>
              )}
              <button type="button" onClick={() => setNote(item.message_id, item.admin_note)}
                style={{ background: 'none', border: 'none', color: '#697386', cursor: 'pointer', padding: 0 }}>
                Заметка
              </button>
              <button type="button" onClick={() => removeFeedback(item.message_id)}
                style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: 0, marginLeft: 'auto' }}>
                Снять оценку
              </button>
              <span style={{ fontSize: 12, color: '#c9cfda', fontFamily: 'monospace' }}>
                {item.session_id}
              </span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default AdminAiSatisfaction;
